import {
  Avatar,
  AvatarFallback,
  BodyText,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import {
  MessageStatus,
  useMessageStatusChange,
} from "@/hooks/useMessageStatusChange";
import { useOpenPatientChart } from "@/hooks/useOpenPatientChart";
import { usePartner } from "@/hooks/usePartner";
import { usePatientTimer } from "@/hooks/usePatientTimer";
import { usePinPatient } from "@/hooks/usePinPatient";
import { useUnpinPatient } from "@/hooks/useUnpinPatient";
import { trackEvent } from "@/lib/tracking";
import { cn, getGenderAbbreviation, getInitials } from "@/lib/utils";
import { selectFallbackPartnerUid } from "@/utils/partnerLogo";
import { format } from "date-fns";
import { Archive, Inbox, Pin } from "lucide-react";
import type { MouseEvent } from "react";
import { Fragment } from "react/jsx-runtime";
import type { AsyncMessageItem } from "./network/useAsyncMessageInbox";

interface MessageRowProps {
  message: Message;
  archived?: boolean;
}

export interface Message extends AsyncMessageItem {
  pinned?: boolean;
}

export const MessageRow = ({ message, archived }: MessageRowProps) => {
  const { patient, encounter } = message;

  const pinPatient = usePinPatient(patient.id);
  const unpinPatient = useUnpinPatient(patient.id);
  const messageStatusChange = useMessageStatusChange(patient.id);
  const openPatientChart = useOpenPatientChart();

  const { partner } = usePartner(
    patient.preferredPartnerUid ??
      selectFallbackPartnerUid(patient.partnerUids),
  );
  const { customerWaitingTimer } = usePatientTimer(message.message.receivedAt);

  const handleClick = async () => {
    trackEvent(TRACKERS.VIEW_ASYNC_MESSAGE);
    try {
      messageStatusChange.mutateAsync({
        patientId: patient.id,
        statusChangeAction: MessageStatus.MARK_READ,
      });
    } catch (error) {
      toast.error("Unable to mark message as read", {
        id: "async-dashboard-mark-read-failed",
      });
    }

    await openPatientChart({
      patientId: patient.id,
      encounterId: encounter.ownAsyncEncounterId,
      medRecordDestination: "timeline",
      chatTab: "async",
    });
  };

  const handleClickArchive = async (e: MouseEvent) => {
    trackEvent(TRACKERS.SEND_TO_ARCHIVE);
    e.stopPropagation();

    try {
      await messageStatusChange.mutateAsync({
        patientId: patient.id,
        statusChangeAction: MessageStatus.MARK_ARCHIVED,
      });
    } catch (error) {
      toast.error("Unable to archive patient", {
        id: "async-dashboard-archive-patient",
      });
    }
  };

  const handleClickPin = async (e: MouseEvent) => {
    e.stopPropagation();

    if (message.pinned) {
      try {
        await unpinPatient.mutateAsync({ patientId: patient.id });
      } catch (error) {
        toast.error("Unable to unpin patient", {
          id: "async-dashboard-unpin-patient",
        });
      }
    } else {
      try {
        await pinPatient.mutateAsync({ patientId: patient.id });
      } catch (error) {
        toast.error("Unable to pin patient", {
          id: "async-dashboard-pin-patient",
        });
      }
    }
  };

  const handleClickInbox = async (e: MouseEvent) => {
    trackEvent(TRACKERS.SEND_TO_INBOX);
    e.stopPropagation();

    try {
      await messageStatusChange.mutateAsync({
        patientId: patient.id,
        statusChangeAction: MessageStatus.MARK_INBOXED,
      });
    } catch (error) {
      toast.error("Unable to inbox patient", {
        id: "async-dashboard-inbox-patient",
      });
    }
  };

  const buttonClassName = cn(
    "rounded-full h-6 w-6 flex justify-center items-center",
    !message.message.unread ? "hover:bg-secondary" : "hover:bg-tertiary",
  );

  return (
    <TableRow
      className={cn(
        "w-12 hover:bg-secondary focus-visible:bg-secondary cursor-pointer",
        !message.message.unread && "bg-secondary hover:bg-tertiary",
      )}
      data-testid={`message-row-${message.patient.id}`}
      onClick={handleClick}
    >
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar className="w-4 h-4">
            <AvatarFallback className="bg-yellow-400">
              <BodyText className="text-[8px]">
                {getInitials(`${patient.nameFirst} ${patient.nameLast}`)}
              </BodyText>
            </AvatarFallback>
          </Avatar>
          <BodyText size="sm" className="leading-tight">
            {patient.nameFirst} {patient.nameLast}
          </BodyText>
        </div>
      </TableCell>
      <TableCell>
        <BodyText size="sm">{`${patient.age}${getGenderAbbreviation(patient.administrativeGender)}`}</BodyText>
      </TableCell>
      <TableCell>
        <BodyText size="sm">{patient.addressState}</BodyText>
      </TableCell>
      <TableCell>
        <BodyText size="sm" className="flex items-center gap-1">
          <img src={partner?.logoUrl} alt={partner?.name} className="w-4 h-4" />
          {partner?.name}
        </BodyText>
      </TableCell>
      <TableCell className="overflow-hidden max-w-64 whitespace-nowrap text-ellipsis">
        <BodyText size="sm" bold={message.message.unread}>
          {message.message.messageBody}
        </BodyText>
      </TableCell>

      <TableCell>
        <div className="float-right flex items-center gap-2 whitespace-nowrap">
          <BodyText size="sm">
            {customerWaitingTimer ||
              format(new Date(message.message.receivedAt), "MM/dd/yyyy")}
          </BodyText>

          {archived ? (
            <Fragment>
              <Tooltip tooltipContentText="Send to Inbox">
                <Button
                  isLoading={messageStatusChange.isPending}
                  variant="icon"
                  data-testid="inbox-message"
                  type="button"
                  onClick={handleClickInbox}
                  className={buttonClassName}
                >
                  <Inbox className="w-3.5 text-link" />
                </Button>
              </Tooltip>
            </Fragment>
          ) : (
            <Fragment>
              <Tooltip tooltipContentText="Send to Archive">
                <Button
                  isLoading={messageStatusChange.isPending}
                  variant="icon"
                  data-testid="archive-message"
                  type="button"
                  onClick={handleClickArchive}
                  className={buttonClassName}
                >
                  <Archive className="w-3.5 text-link" />
                </Button>
              </Tooltip>

              <Tooltip tooltipContentText={message.pinned ? "Unpin" : "Pin"}>
                <Button
                  isLoading={pinPatient.isPending}
                  variant="icon"
                  data-testid="pin-message"
                  type="button"
                  onClick={handleClickPin}
                  className={buttonClassName}
                >
                  <Pin
                    data-testid="pin-icon"
                    className={cn(
                      "w-3.5 text-link",
                      message.pinned && "fill-link",
                    )}
                  />
                </Button>
              </Tooltip>
            </Fragment>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
